/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMount } from 'ahooks';
import cx from 'classnames';
import { parseISO } from 'date-fns';
import get from 'lodash/get';
import { FC, useState } from 'react';
import Collapsible from 'react-collapsible';
import { useTranslation } from 'react-i18next';
import { Card, Header, Icon } from 'semantic-ui-react';

import { fetchAgenda } from '../../../agenda/store/agenda.actions';
import { bem } from '../../../core/design/bem';
import store from '../../../shared/Store';
import { fetchAppointments } from '../../../store/actions';
import Images from '../../../utils/Images';
import { localeFormat } from '../../../utils/date';
import BlockContainer from '../../BlockContainer';
import CdnImage from '../../CdnImage';
import HtmlTemplate from '../../HtmlTemplate';
import ImageIcon from '../../ImageIcon';
import WorkshopSessionsCard from '../../workshops/WorkshopSessionsCard';
import { ContainerProps } from '../types';
import './ProgramBlock.scss';
import { useProgramAgenda } from './hooks/useProgramAgenda';
import {
  Day,
  DayProgramProps,
  ItemTagLine,
  ProgramConfig,
  ProgramItem,
} from './types/program.types';

const css = bem('ProgramBlock');
const translationPrefix = 'blocks.program';

const ItemTagLineComp: FC<{ item: Record<string, any>; tagLine: ItemTagLine }> = ({
  item,
  tagLine,
}) => {
  if (!tagLine?.fields?.length) return null;
  // Keep fields with string or array length
  const validFields = tagLine.fields.filter((field) => get(item, field.field)?.length);
  if (!validFields.length) return null;

  const renderValue = (field: string, value: string) => {
    return (
      <div
        key={`${field}-${value}`}
        className="item__tag-line__field"
        data-tag-field={field}
        data-tag-value={value}
      >
        {value}
      </div>
    );
  };

  return (
    <div className="item__tag-line">
      {validFields.map((field) => {
        const value = get(item, field.field);
        if (Array.isArray(value)) {
          return value.map((v) => renderValue(field.field, v));
        }
        return renderValue(field.field, value);
      })}
    </div>
  );
};

const ItemTags: FC<{ item: Record<string, any>; tags?: ItemTagLine[] }> = ({ item, tags }) => {
  if (!tags?.length) return null;

  return (
    <div className="item__tags">
      {tags.map((tag) => (
        <ItemTagLineComp item={item} tagLine={tag} />
      ))}
    </div>
  );
};

const DayProgram = (props: DayProgramProps): JSX.Element => {
  const { program, config = {} } = props;
  const { imageProps = {}, defaultOpen = false, item: itemConfig = {} } = config;
  const { _id, date, items = [], image: programImage, icon: dayIcon, sideTitle = '' } = program;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const { t } = useTranslation();
  const dateLabel = t(`${translationPrefix}.date`, { date });
  const hasContent = items.length > 0 || !!programImage?.uri;

  const renderItem = (item: ProgramItem, index: number) => {
    const {
      _id: itemId,
      title,
      icon,
      subtitle,
      location,
      description,
      time,
      endTime,
      image,
    } = item;

    return (
      <div
        className={cx('item', itemId, {
          'has-image': !!image?.uri && itemConfig?.showImage,
        })}
        key={itemId || index}
      >
        {icon && <ImageIcon icon={icon} className="icon" maxHeight={30} />}
        <div className="content">
          {!!time && (
            <div className="time">
              {time}
              {itemConfig?.showEndTime && !!endTime && ` - ${endTime}`}
            </div>
          )}
          <Header as="h4" className="title">
            {title}
          </Header>
          {!!subtitle && (
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={{ __html: subtitle }} className="subtitle" />
          )}
          <ItemTags item={item} tags={itemConfig?.tags} />
          {!!location && (
            <div className="location">
              <Icon name="map marker alternate" /> {location}{' '}
            </div>
          )}
          {itemConfig?.showDescription && !!description && (
            <HtmlTemplate as="p" item={undefined} className="description" template={description} />
          )}
        </div>
        {itemConfig?.showImage && Images.exists(image) && (
          <div className="content content--image">
            <CdnImage className="image" src={image} maxWidth={600} {...itemConfig?.imageProps} />
          </div>
        )}
      </div>
    );
  };

  return (
    <Collapsible
      open={isOpen}
      trigger={
        <div className={css('day', { _id })}>
          {!!dayIcon && <ImageIcon icon={dayIcon} className="icon" maxHeight={30} />}
          {!!sideTitle && (
            <Header as="h4" className="side">
              {sideTitle}
            </Header>
          )}
          <Header as="h3" className="date">
            {dateLabel}
          </Header>
          {hasContent && (
            <Icon className="chevron" name={!isOpen ? 'chevron down' : 'chevron up'} />
          )}
        </div>
      }
      transitionTime={200}
      onOpening={() => setIsOpen(true)}
      onClosing={() => setIsOpen(false)}
    >
      {hasContent && (
        <div className={css('details')}>
          <div className="items">{items.map(renderItem)}</div>
          {programImage?.uri && (
            <div className="image">
              <CdnImage src={programImage} maxWidth={500} {...imageProps} />
            </div>
          )}
        </div>
      )}
    </Collapsible>
  );
};

DayProgram.defaultProps = {
  config: {},
};

type ProgramBlockProps = {
  container?: ContainerProps;
  days: Day[];
  mode?: 'agenda' | 'custom';
  config?: ProgramConfig;
};

const ProgramBlock = (props: ProgramBlockProps): JSX.Element => {
  const { container, config, mode, days: defaultDays } = props;
  const days = useProgramAgenda(mode || 'custom', defaultDays, config);

  const { dateFormat = 'PPPP', item: itemConfig, template } = config || {};
  const { variant = 'classic' } = template || {};
  const { blocks, showEndTime = false, showLocation = false } = itemConfig || {};

  useMount(() => {
    store.reduxStore.dispatch(fetchAppointments());
    store.reduxStore.dispatch(fetchAgenda());
  });

  return (
    <BlockContainer className={css()} {...container}>
      {days.map((program) => {
        if (variant === 'classic') {
          return <DayProgram key={program._id || program.date} program={program} config={config} />;
        }

        return (
          <div className={css(variant).toString()}>
            <Header as="h3" className="date">
              {localeFormat(parseISO(program.date), dateFormat)}
            </Header>
            <Card.Group className={css('card').toString()} itemsPerRow={3} centered>
              {program?.items?.map((item: any) => (
                <WorkshopSessionsCard
                  blocks={blocks}
                  workshop={item}
                  showEndTime={showEndTime}
                  showLocation={showLocation}
                />
              ))}
            </Card.Group>
          </div>
        );
      })}
    </BlockContainer>
  );
};

ProgramBlock.defaultProps = {
  container: {},
  config: {},
  mode: 'custom',
};

export default ProgramBlock;
